/**
 * Keyboard Shortcuts Modal
 *
 * Displays available keyboard shortcuts for the workbench
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';

/**
 * Keyboard Shortcuts Modal
 */
// eslint-disable-next-line max-lines-per-function
function KeyboardShortcutsModal({ open, onClose, codesByShortcut }) {
  const shortcuts = [
    {
      category: 'Navigation',
      items: [
        { keys: ['N', 'Enter'], description: 'Move to next chunk' },
        { keys: ['P'], description: 'Move to previous chunk' },
        { keys: ['Shift', 'N'], description: 'Next document' },
        { keys: ['Shift', 'P'], description: 'Previous document' },
      ],
    },
    {
      category: 'Actions',
      items: [
        { keys: ['1-9'], description: 'Apply code by shortcut number' },
        { keys: ['Ctrl', 'F'], description: 'Focus code search' },
        { keys: ['?'], description: 'Show this shortcuts modal' },
        { keys: ['Esc'], description: 'Close modal / clear selection' },
      ],
    },
    {
      category: 'AI Suggestions',
      items: [
        { keys: ['Alt', 'A'], description: 'Accept first AI suggestion' },
        { keys: ['Alt', 'X'], description: 'Reject first AI suggestion' },
        { keys: ['Alt', 'G'], description: 'Get/Generate AI suggestions' },
      ],
    },
  ];

  // Get codes with shortcuts
  const codeShortcuts = Object.entries(codesByShortcut).map(([key, code]) => ({
    key,
    code: code.displayName,
    valence: code.valence,
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <KeyboardIcon />
        Keyboard Shortcuts
      </DialogTitle>

      <DialogContent dividers>
        {shortcuts.map((section) => (
          <Box key={section.category} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                mb: 1.5,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: 0.5,
              }}
            >
              {section.category}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {section.items.map((item, i) => (
                <ShortcutRow
                  key={i}
                  keys={item.keys}
                  description={item.description}
                  disabled={item.disabled}
                />
              ))}
            </Box>
          </Box>
        ))}

        {/* Code Shortcuts */}
        {codeShortcuts.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                  letterSpacing: 0.5,
                }}
              >
                Quick Apply Codes
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {codeShortcuts.map((item) => (
                  <ShortcutRow
                    key={item.key}
                    keys={[item.key]}
                    description={item.code}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Shortcut Row Component
 */
function ShortcutRow({ keys, description, disabled = false }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.5,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: disabled ? 'text.disabled' : 'text.primary',
        }}
      >
        {description}
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {keys.map((key, i) => (
          <React.Fragment key={key}>
            {i > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mx: 0.25 }}>
                +
              </Typography>
            )}
            <Chip
              label={key}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: 'rgba(0,0,0,0.08)',
                color: 'text.primary',
                borderRadius: 1,
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

export default KeyboardShortcutsModal;
